/**
 * The decisions somebody is allowed to change without a deployment.
 *
 * WHY THIS SCREEN DID NOT EXIST
 * -----------------------------
 * `system_settings` has been in the schema since the beginning, seeded with
 * eleven real decisions — how much a cashier may discount before a manager is
 * called, how much cash a rider may carry, whether a shift must be open before
 * anything can be sold — and no screen ever showed them. They were in force
 * and could not be read, never mind changed, without opening the database.
 *
 * A rule nobody can read is a rule nobody can agree to. The ten per cent
 * discount limit is enforced on every order; until now the only way to find
 * out it was ten was to exceed it.
 *
 * WHY THEY ARE GROUPED
 * --------------------
 * Eleven switches in a column invites changing the one next to the one you
 * meant. What the till does and how stock is watched are separate
 * conversations, so they are separate sections.
 *
 * WHY EACH SAVES ON ITS OWN
 * -------------------------
 * There is no "save all". Each of these is a decision in its own right, and a
 * single button that writes eleven of them at once is a button nobody can
 * press confidently — and an audit line that cannot say which one somebody
 * actually meant to change.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Link } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

/** What each group of settings is actually about, in the shop's words. */
const SECTIONS = {
  pos: 'The till',
  cashier: 'Shifts',
  delivery: 'Deliveries',
  inventory: 'Stock',
  purchasing: 'Purchasing',
  grn: 'Deliveries in',
  general: 'The business',
  online: 'The website',
};

/**
 * Plain descriptions, because a key is not an explanation.
 *
 * `max_discount_without_approval` tells somebody who already knows what it
 * does. Anybody else needs the sentence.
 */
const EXPLAINS = {
  max_discount_without_approval:
    'How much a cashier may take off before a manager has to approve it, as a percentage.',
  require_approval_to_change_orders:
    'A cashier needs a manager’s PIN to cancel an order, or to take an item off one already sent or lower its quantity. Adding items, raising a quantity, or changing the type or table do not ask. A manager or owner doing it themselves is not asked.',
  rider_cod_limit: 'The most cash a new rider may carry. Each rider can be given their own.',
  shift_mandatory: 'A till has to have a shift open before it can sell anything.',
  auto_print_receipt: 'Print a receipt as soon as an order is paid for.',
  tax_inclusive_pricing: 'Menu prices already include tax, rather than having it added.',
  low_stock_alert_enabled: 'Tell a manager when an ingredient is running out.',
  order_number_prefix: 'The letters at the front of every order number.',
  po_number_prefix: 'The letters at the front of every purchase order.',
  grn_number_prefix: 'The letters at the front of every goods-received note.',
  company_name: 'Printed at the top of every receipt.',
  default_currency: 'The currency everything is priced in.',
};

const field =
  'h-10 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

export default function SettingsPage() {
  const client = useQueryClient();

  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.get('/admin/settings'),
  });

  if (settings.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner label="Reading the settings" />
      </div>
    );
  }

  if (settings.isError) {
    return (
      <p className="py-16 text-center text-sm text-ink-2">
        {settings.error?.status === 403
          ? 'These are the owner’s settings. Ask them to change one for you.'
          : 'Could not read the settings. Check the branch computer is running.'}
      </p>
    );
  }

  const rows = settings.data?.settings ?? [];

  const byModule = rows.reduce((groups, row) => {
    (groups[row.module] ??= []).push(row);
    return groups;
  }, {});

  return (
    <div className="mx-auto max-w-2xl">
      <header className="mb-5">
        <h1 className="font-semibold text-xl text-ink">Settings</h1>
        <p className="mt-0.5 text-sm text-ink-2">
          Rules the shop runs on. Each one takes effect on the next order.
        </p>
      </header>

      {/*
        The tax rate is not one of these switches — it lives on its own screen,
        with the history and the "who may change it" that a rate needs. It was
        reachable only by knowing the URL, so people came here looking for it.
      */}
      <Link
        to="/settings/tax"
        className="mb-6 flex items-center justify-between rounded-xl border border-rule bg-surface px-4 py-3 hover:border-brand-400"
      >
        <span>
          <span className="block text-sm font-medium text-ink">Tax rate</span>
          <span className="block text-xs text-ink-3">
            The sales tax charged on every bill.
          </span>
        </span>
        <span aria-hidden="true" className="text-ink-3">
          &rarr;
        </span>
      </Link>

      <div className="space-y-6">
        {Object.entries(byModule).map(([module, group]) => (
          <section key={module}>
            <h2 className="mb-2 text-sm font-medium text-ink-2">
              {SECTIONS[module] ?? module}
            </h2>

            <div className="space-y-2">
              {group.map((row) => (
                <Setting
                  key={row.key}
                  setting={row}
                  onSaved={() => client.invalidateQueries({ queryKey: ['admin', 'settings'] })}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Setting({ setting, onSaved }) {
  const [value, setValue] = useState(setting.value);

  const save = useMutation({
    mutationFn: (next) => api.put(`/admin/settings/${setting.key}`, { value: next }),
    onSuccess: onSaved,
  });

  const changed = String(value) !== String(setting.value);
  const isSwitch = setting.type === 'boolean';

  return (
    <div className="rounded-xl border border-rule bg-surface p-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink">
            {EXPLAINS[setting.key] ?? setting.description ?? setting.key}
          </p>
          {/* The key itself, quietly — it is what appears in the audit log and
              what somebody would search for if they had to. */}
          <p className="mt-0.5 font-mono text-xs text-ink-3">{setting.key}</p>
        </div>

        {isSwitch ? (
          <button
            role="switch"
            aria-checked={value === 'true'}
            aria-label={setting.key}
            onClick={() => {
              const next = value === 'true' ? 'false' : 'true';
              setValue(next);
              save.mutate(next);
            }}
            className={`h-7 w-12 shrink-0 rounded-full transition-colors ${
              value === 'true' ? 'bg-brand-400' : 'bg-surface-3'
            }`}
          >
            <span
              className={`block h-5 w-5 rounded-full bg-surface transition-transform ${
                value === 'true' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ) : (
          <>
            <input
              type={setting.type === 'number' ? 'number' : 'text'}
              min={setting.type === 'number' ? '0' : undefined}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label={setting.key}
              className={`${field} w-32 ${setting.type === 'number' ? 'text-right tabular-nums' : ''}`}
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={!changed || save.isPending}
              onClick={() => save.mutate(value)}
            >
              {save.isPending ? 'Saving' : 'Save'}
            </Button>
          </>
        )}
      </div>

      {save.isError && (
        <p className="mt-2 text-sm text-bad">{save.error?.message ?? 'That did not work.'}</p>
      )}
    </div>
  );
}
